import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import type {
  AdminAggregateJob,
  AdminCompanyDetail,
  AdminCompanyRow,
  AdminErrorDetail,
  AdminErrorListFilters,
  AdminErrorSortBy,
  AdminListErrorIssuesResult,
  AdminListErrorsResult,
  AdminListWorkspacesResult,
  AdminServiceAdminRow,
  AdminSyncUserLastActiveResult,
  AdminUpdateErrorResolvedResult,
  AdminUserDetail,
  AdminUserRow,
  AdminUserSearchMode,
  AdminWorkspaceDetail,
  DashboardStatsData,
  AdminPurgeUserResult,
  AdminPurgeWorkspaceResult,
} from './types';

export const adminGetDashboardStats = httpsCallable<
  { filterSeriesId?: string | null },
  DashboardStatsData
>(functions, 'adminGetDashboardStatsFn');

export const adminListWorkspaces = httpsCallable<
  {
    cursor?: string | null;
    pageSize?: number;
    filterPlanId?: string | null;
    filterCompanyId?: string | null;
    filterBillingStatus?: string | null;
    search?: string | null;
    includeMemberCount?: boolean;
  },
  AdminListWorkspacesResult
>(functions, 'adminListWorkspacesFn');

export const adminGetWorkspaceStats = httpsCallable<{ workspaceId: string }, AdminWorkspaceDetail>(
  functions,
  'adminGetWorkspaceStatsFn'
);

export const adminListCompanies = httpsCallable<
  unknown,
  { rows: AdminCompanyRow[]; totalCount: number; generatedAt: number }
>(functions, 'adminListCompaniesFn');

export const adminGetCompanyDetail = httpsCallable<{ companyId: string }, AdminCompanyDetail>(
  functions,
  'adminGetCompanyDetailFn'
);

export const adminSearchUsers = httpsCallable<
  { mode: AdminUserSearchMode; query?: string },
  { rows: AdminUserRow[]; generatedAt: number }
>(functions, 'adminSearchUsersFn');

export const adminGetUserDetail = httpsCallable<{ uid: string }, AdminUserDetail>(
  functions,
  'adminGetUserDetailFn'
);

export const adminListServiceAdmins = httpsCallable<unknown, { rows: AdminServiceAdminRow[] }>(
  functions,
  'adminListServiceAdminsFn'
);

export const addServiceAdmin = httpsCallable<
  { email: string; note?: string },
  { result: 'success' | 'already_admin'; uid: string }
>(functions, 'addServiceAdminFn');

export const removeServiceAdmin = httpsCallable<
  { uid: string },
  { result: 'success' | 'not_found' }
>(functions, 'removeServiceAdminFn');

export const adminSyncUserLastActive = httpsCallable<unknown, AdminSyncUserLastActiveResult>(
  functions,
  'adminSyncUserLastActiveFn'
);

export const adminRecomputeDashboard = httpsCallable<
  unknown,
  { jobId: string; alreadyRunning: boolean }
>(functions, 'adminRecomputeDashboardFn');

export const adminGetAggregateJob = httpsCallable<
  { jobId: string },
  { job: AdminAggregateJob | null }
>(functions, 'adminGetAggregateJobFn');

export const adminListErrors = httpsCallable<
  {
    filters?: AdminErrorListFilters;
    sortBy?: AdminErrorSortBy;
    pageSize?: number;
    cursor?: string | null;
  },
  AdminListErrorsResult
>(functions, 'adminListErrorsFn');

export const adminListErrorIssues = httpsCallable<
  {
    filters?: Pick<
      AdminErrorListFilters,
      'source' | 'severity' | 'priority' | 'publishId' | 'workspaceId' | 'fromMs' | 'toMs'
    >;
    limit?: number;
  },
  AdminListErrorIssuesResult
>(functions, 'adminListErrorIssuesFn');

export const adminGetErrorDetail = httpsCallable<{ errorId: string }, AdminErrorDetail>(
  functions,
  'adminGetErrorDetailFn'
);

export const adminUpdateErrorResolved = httpsCallable<
  | { errorId: string; resolved: boolean }
  | { source: string; code: string; resolved: boolean },
  AdminUpdateErrorResolvedResult
>(functions, 'adminUpdateErrorResolvedFn');

export const adminPurgeWorkspace = httpsCallable<
  { workspaceId: string; confirm?: string; dryRun?: boolean },
  AdminPurgeWorkspaceResult
>(functions, 'adminPurgeWorkspaceFn', { timeout: 540000 });

export const adminPurgeUser = httpsCallable<
  { uid: string; confirm?: string; dryRun?: boolean },
  AdminPurgeUserResult
>(functions, 'adminPurgeUserFn', { timeout: 540000 });

// ---- Offer Entitlement ----
import type {
  AdminGetOfferCatalogResult,
  AdminGetWorkspaceOverrideResult,
  WorkspaceEntitlementOverride,
} from './types';
import type { PlanId, SeriesId } from '../plans/entitlements';

export const adminGetOfferCatalog = httpsCallable<unknown, AdminGetOfferCatalogResult>(
  functions,
  'adminGetOfferCatalogFn'
);

export const adminGetWorkspaceOverride = httpsCallable<
  { workspaceId: string },
  AdminGetWorkspaceOverrideResult
>(functions, 'adminGetWorkspaceOverrideFn');

export const adminSetWorkspaceOverride = httpsCallable<
  { workspaceId: string; override: WorkspaceEntitlementOverride | null },
  { ok: boolean; changedKeys: string[] }
>(functions, 'adminSetWorkspaceOverrideFn');

export const adminSetWorkspacePlan = httpsCallable<
  { workspaceId: string; planId: PlanId; seriesId: SeriesId },
  { ok: boolean; planId: PlanId; seriesId: SeriesId; offerId: string }
>(functions, 'adminSetWorkspacePlanFn');

export const adminMoveWorkspaceToRootParent = httpsCallable<
  { workspaceId: string },
  { ok: boolean; workspaceId: string; fromParentId: string; toParentId: string }
>(functions, 'adminMoveWorkspaceToRootParentFn');

export const adminSwapWorkspaceWithParent = httpsCallable<
  { workspaceId: string },
  {
    ok: boolean;
    workspaceId: string;
    parentId: string;
    planId: string;
    seriesId: string;
    movedSiblingIds: string[];
  }
>(functions, 'adminSwapWorkspaceWithParentFn');

export const adminDetachWorkspaceFromParent = httpsCallable<
  { workspaceId: string; planId: string },
  { ok: boolean; workspaceId: string; fromParentId: string; planId: string }
>(functions, 'adminDetachWorkspaceFromParentFn');
